/**
 * GMCT Data Import Script
 * 
 * This script imports GMCT-specific knowledge base data including:
 * - Cemetery information and policies
 * - Service offerings and procedures
 * - Location details and directions
 * - FAQ responses for common questions
 * 
 * Usage:
 * 1. Browser Console: Copy and paste this script
 * 2. React Component: Import and call importGMCTData()
 * 3. Direct execution: Run the main import function
 */

// GMCT Sample Data Array
const GMCT_SAMPLE_DATA = [
  // Cemetery Information
  {
    content: "Greater Metropolitan Cemeteries Trust (GMCT) serves Melbourne's northern and western communities with compassionate cemetery services. We operate three main locations: Altona Memorial Park, Fawkner Memorial Park, and Emerald Cemetery.",
    category: "general",
    type: "information",
    source: "website"
  },
  {
    content: "GMCT has been serving families for over 50 years, providing dignified burial, cremation, and memorial services. Our experienced team understands the importance of honoring your loved ones with care and respect.",
    category: "general",
    type: "information",
    source: "about"
  },
  
  // Location Information
  {
    content: "Altona Memorial Park is our main location at 123 Memorial Drive, Altona VIC 3018. Phone: (03) 8359 0111. Open Monday-Friday 9AM-5PM, Saturday-Sunday 9AM-4PM. This is our largest facility with full chapel services.",
    category: "location",
    type: "location",
    source: "locations"
  },
  {
    content: "Fawkner Memorial Park is located at 456 Cemetery Road, Fawkner VIC 3060. Phone: (03) 8359 0112. Open Monday-Friday 9AM-5PM, Saturday-Sunday 9AM-4PM. Features beautiful garden settings and memorial walls.",
    category: "location",
    type: "location",
    source: "locations"
  },
  {
    content: "Emerald Cemetery is situated at 789 Emerald Hill, Emerald VIC 3782. Phone: (03) 8359 0113. Open Monday-Friday 9AM-4PM, Saturday-Sunday 10AM-3PM. Our heritage cemetery with natural bushland settings.",
    category: "location",
    type: "location",
    source: "locations"
  },
  
  // Services
  {
    content: "GMCT offers comprehensive burial services including traditional earth burial, lawn burial, and mausoleum entombment. We provide a range of burial plot options to suit different preferences and budgets.",
    category: "services",
    type: "service",
    source: "services"
  },
  {
    content: "Our cremation services include cremation with memorial services, direct cremation, and scattering of ashes in our memorial gardens. We have modern crematorium facilities at Altona Memorial Park.",
    category: "services",
    type: "service",
    source: "services"
  },
  {
    content: "Memorial services can be held in our Chapel of Repose at Altona Memorial Park or in our outdoor memorial areas. We can accommodate both religious and non-religious ceremonies.",
    category: "services",
    type: "service",
    source: "services"
  },
  {
    content: "Pre-planning services allow families to make arrangements in advance, reducing stress during difficult times. Our pre-planning consultants can help you understand options and costs.",
    category: "services",
    type: "service",
    source: "services"
  },
  
  // Policies and Procedures
  {
    content: "Visiting hours are 9AM-5PM Monday-Friday and 9AM-4PM weekends at Altona and Fawkner. Emerald Cemetery is open 9AM-4PM weekdays and 10AM-3PM weekends. Gates are locked outside these hours for security.",
    category: "policy",
    type: "policy",
    source: "policies"
  },
  {
    content: "Fresh flowers are welcome on graves at any time. Artificial flowers are permitted but must be in good condition. We remove wilted flowers and decorations during regular maintenance.",
    category: "policy",
    type: "policy",
    source: "policies"
  },
  {
    content: "Memorial plaques and headstones must be approved by GMCT before installation. We have specific guidelines for materials, sizes, and inscriptions to maintain the dignity of our cemeteries.",
    category: "policy",
    type: "policy",
    source: "policies"
  },
  {
    content: "Pets are not permitted in cemetery grounds except for registered assistance animals. This policy helps maintain the peaceful environment for all visitors.",
    category: "policy",
    type: "policy",
    source: "policies"
  },
  
  // Contact and Emergency Information
  {
    content: "For immediate assistance or emergencies, call our 24/7 emergency line at (03) 8359 0100. Our after-hours service can help with urgent matters and provide support when needed.",
    category: "contact",
    type: "contact",
    source: "contact"
  },
  {
    content: "Main office contact: (03) 8359 0111, email: info@gmct.com.au. Our customer service team is available Monday-Friday 9AM-5PM to assist with inquiries and appointments.",
    category: "contact",
    type: "contact",
    source: "contact"
  },
  
  // Pricing and Costs
  {
    content: "Burial plot prices vary by location and type. Lawn burial plots start from $3,500, traditional burial plots from $4,200, and mausoleum spaces from $8,500. Prices include perpetual care.",
    category: "pricing",
    type: "pricing",
    source: "pricing"
  },
  {
    content: "Cremation services start from $1,800 for direct cremation. Memorial services with cremation start from $2,500. All cremation fees include the cremation process and basic urn.",
    category: "pricing",
    type: "pricing",
    source: "pricing"
  },
  
  // Maintenance and Care
  {
    content: "GMCT provides perpetual care for all burial sites, including lawn mowing, garden maintenance, and general upkeep. Families can request additional maintenance services for specific grave sites.",
    category: "maintenance",
    type: "service",
    source: "maintenance"
  },
  {
    content: "To request maintenance or report issues, use our online work request system or call (03) 8359 0111. Common requests include headstone cleaning, garden care, and memorial repairs.",
    category: "maintenance",
    type: "service",
    source: "maintenance"
  }
];

// FAQ Data for Common Questions
const GMCT_FAQ_DATA = [
  {
    question: "What are your visiting hours?",
    answer: "Our visiting hours are 9AM-5PM Monday-Friday and 9AM-4PM on weekends at Altona Memorial Park and Fawkner Memorial Park. Emerald Cemetery is open 9AM-4PM weekdays and 10AM-3PM weekends.",
    category: "faq",
    type: "policy"
  },
  {
    question: "How do I find a specific grave?",
    answer: "You can search for graves using our online search system or contact our office at (03) 8359 0111. Our staff can provide plot locations and directions to help you find your loved one's resting place.",
    category: "faq",
    type: "service"
  },
  {
    question: "Can I bring flowers to the cemetery?",
    answer: "Yes, fresh flowers are always welcome. Artificial flowers are permitted but must be in good condition. We remove wilted flowers during regular maintenance to keep the cemetery looking respectful.",
    category: "faq",
    type: "policy"
  },
  {
    question: "What cremation services do you offer?",
    answer: "We offer direct cremation starting from $1,800, cremation with memorial services from $2,500, and ash scattering in our memorial gardens. Our modern crematorium is located at Altona Memorial Park.",
    category: "faq",
    type: "service"
  },
  {
    question: "How much do burial plots cost?",
    answer: "Burial plot prices vary by location and type. Lawn burial plots start from $3,500, traditional burial plots from $4,200, and mausoleum spaces from $8,500. All prices include perpetual care.",
    category: "faq",
    type: "pricing"
  },
  {
    question: "Do you offer pre-planning services?",
    answer: "Yes, we offer comprehensive pre-planning services to help families make arrangements in advance. This reduces stress during difficult times and allows you to make informed decisions about your preferences.",
    category: "faq",
    type: "service"
  },
  {
    question: "What are your emergency contact details?",
    answer: "For emergencies, call our 24/7 emergency line at (03) 8359 0100. For general inquiries, contact our main office at (03) 8359 0111 or email info@gmct.com.au during business hours.",
    category: "faq",
    type: "contact"
  },
  {
    question: "Can I request maintenance for a grave site?",
    answer: "Yes, you can request maintenance through our online work request system or by calling (03) 8359 0111. We offer services like headstone cleaning, garden care, and memorial repairs.",
    category: "faq",
    type: "service"
  },
  {
    question: "Where are your cemetery locations?",
    answer: "GMCT operates three locations: Altona Memorial Park (123 Memorial Drive, Altona), Fawkner Memorial Park (456 Cemetery Road, Fawkner), and Emerald Cemetery (789 Emerald Hill, Emerald).",
    category: "faq",
    type: "location"
  },
  {
    question: "What memorial service options are available?",
    answer: "We offer memorial services in our Chapel of Repose at Altona Memorial Park or in outdoor memorial areas. We can accommodate both religious and non-religious ceremonies according to your preferences.",
    category: "faq",
    type: "service"
  }
];

// Helper Functions

/**
 * Categorizes content based on keywords and context
 */
function categorizeContent(content: string): {
  category: string;
  type: 'policy' | 'service' | 'location' | 'general' | 'pricing' | 'contact';
} {
  const lowerContent = content.toLowerCase();
  
  // Policy-related content
  if (lowerContent.includes('policy') || lowerContent.includes('rule') || 
      lowerContent.includes('visiting hours') || lowerContent.includes('permitted') ||
      lowerContent.includes('guidelines') || lowerContent.includes('must be')) {
    return { category: 'policy', type: 'policy' };
  }
  
  // Service-related content
  if (lowerContent.includes('service') || lowerContent.includes('burial') || 
      lowerContent.includes('cremation') || lowerContent.includes('memorial') ||
      lowerContent.includes('funeral') || lowerContent.includes('pre-planning')) {
    return { category: 'services', type: 'service' };
  }
  
  // Location-related content
  if (lowerContent.includes('location') || lowerContent.includes('address') || 
      lowerContent.includes('altona') || lowerContent.includes('fawkner') ||
      lowerContent.includes('emerald') || lowerContent.includes('memorial park')) {
    return { category: 'location', type: 'location' };
  }
  
  // Pricing-related content
  if (lowerContent.includes('price') || lowerContent.includes('cost') || 
      lowerContent.includes('$') || lowerContent.includes('fee')) {
    return { category: 'pricing', type: 'pricing' };
  }
  
  // Contact-related content
  if (lowerContent.includes('phone') || lowerContent.includes('email') || 
      lowerContent.includes('contact') || lowerContent.includes('call') ||
      lowerContent.includes('emergency')) {
    return { category: 'contact', type: 'contact' };
  }
  
  // Default to general information
  return { category: 'general', type: 'general' };
}

/**
 * Extracts specific information from content
 */
function extractMetadata(content: string, source: string = 'import'): any {
  const metadata: any = {
    source,
    imported_at: new Date().toISOString(),
    content_length: content.length
  };
  
  // Extract phone numbers
  const phoneMatch = content.match(/\(?\d{2}\)?\s?\d{4}\s?\d{4}/g);
  if (phoneMatch) {
    metadata.phone_numbers = phoneMatch;
  }
  
  // Extract addresses
  const addressMatch = content.match(/\d+\s+[A-Za-z\s]+,\s+[A-Za-z\s]+\s+VIC\s+\d{4}/g);
  if (addressMatch) {
    metadata.addresses = addressMatch;
  }
  
  // Extract times/hours
  const timeMatch = content.match(/\d{1,2}(AM|PM)-\d{1,2}(AM|PM)/g);
  if (timeMatch) {
    metadata.operating_hours = timeMatch;
  }
  
  // Extract prices
  const priceMatch = content.match(/\$[\d,]+/g);
  if (priceMatch) {
    metadata.prices = priceMatch;
  }
  
  return metadata;
}

/**
 * Processes raw data into knowledge chunks
 */
function processDataIntoChunks(rawData: any[]): any[] {
  return rawData.map((item, index) => {
    const { category, type } = item.category && item.type ? 
      { category: item.category, type: item.type } : 
      categorizeContent(item.content || item.answer || '');
    
    const content = item.content || 
      (item.question && item.answer ? `Q: ${item.question}\nA: ${item.answer}` : '');
    
    return {
      content: content.trim(),
      metadata: {
        ...extractMetadata(content, item.source || 'import'),
        category,
        type,
        original_index: index,
        question: item.question || null,
        answer: item.answer || null
      }
    };
  }).filter(chunk => chunk.content.length > 10); // Filter out empty or too-short chunks
}

/**
 * Calls the API to process knowledge chunks
 */
async function processKnowledgeChunks(
  chunks: any[], 
  cemetery_id: string = 'gmct',
  onProgress?: (processed: number, total: number) => void
): Promise<any> {
  const batchSize = 10; // Process in batches to avoid overwhelming the API
  const results = {
    total_processed: 0,
    total_failed: 0,
    errors: [] as string[],
    batches: [] as any[]
  };
  
  console.log(`🚀 Starting to process ${chunks.length} knowledge chunks for ${cemetery_id}`);
  
  // Process in batches
  for (let i = 0; i < chunks.length; i += batchSize) {
    const batch = chunks.slice(i, i + batchSize);
    const batchNumber = Math.floor(i / batchSize) + 1;
    const totalBatches = Math.ceil(chunks.length / batchSize);
    
    console.log(`📦 Processing batch ${batchNumber}/${totalBatches} (${batch.length} chunks)`);
    
    try {
      const response = await fetch('/api/process-knowledge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          cemetery_id,
          chunks: batch
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      results.total_processed += result.processed || 0;
      results.total_failed += result.failed || 0;
      results.errors.push(...(result.errors || []));
      results.batches.push({
        batch: batchNumber,
        ...result
      });
      
      console.log(`✅ Batch ${batchNumber} complete: ${result.processed} processed, ${result.failed} failed`);
      
      // Call progress callback
      if (onProgress) {
        onProgress(results.total_processed, chunks.length);
      }
      
      // Add delay between batches to be respectful to the API
      if (i + batchSize < chunks.length) {
        console.log('⏳ Waiting 2 seconds before next batch...');
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
    } catch (error) {
      console.error(`❌ Batch ${batchNumber} failed:`, error);
      results.total_failed += batch.length;
      results.errors.push(`Batch ${batchNumber}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
}

/**
 * Main import function
 */
export async function importGMCTData(
  cemetery_id: string = 'gmct',
  onProgress?: (processed: number, total: number, status: string) => void
): Promise<any> {
  try {
    console.log('🏗️ Starting GMCT data import process...');
    
    // Update progress
    if (onProgress) onProgress(0, 0, 'Preparing data...');
    
    // Combine all data sources
    const allRawData = [
      ...GMCT_SAMPLE_DATA,
      ...GMCT_FAQ_DATA
    ];
    
    console.log(`📊 Total raw data items: ${allRawData.length}`);
    
    // Process data into knowledge chunks
    if (onProgress) onProgress(0, 0, 'Processing data into chunks...');
    const processedChunks = processDataIntoChunks(allRawData);
    
    console.log(`🔄 Processed into ${processedChunks.length} knowledge chunks`);
    console.log('📋 Chunk categories:', 
      [...new Set(processedChunks.map(c => c.metadata.category))].join(', ')
    );
    
    // Import chunks with progress tracking
    if (onProgress) onProgress(0, processedChunks.length, 'Importing chunks...');
    
    const results = await processKnowledgeChunks(
      processedChunks, 
      cemetery_id,
      (processed, total) => {
        if (onProgress) onProgress(processed, total, `Importing... ${processed}/${total}`);
      }
    );
    
    // Final results
    console.log('🎉 Import complete!');
    console.log(`✅ Successfully processed: ${results.total_processed}`);
    console.log(`❌ Failed: ${results.total_failed}`);
    
    if (results.errors.length > 0) {
      console.log('⚠️ Errors encountered:');
      results.errors.forEach((error, index) => {
        console.log(`  ${index + 1}. ${error}`);
      });
    }
    
    if (onProgress) {
      onProgress(
        results.total_processed, 
        processedChunks.length, 
        `Complete! ${results.total_processed} imported, ${results.total_failed} failed`
      );
    }
    
    return {
      success: results.total_processed > 0,
      total_chunks: processedChunks.length,
      processed: results.total_processed,
      failed: results.total_failed,
      errors: results.errors,
      batches: results.batches
    };
    
  } catch (error) {
    console.error('💥 Import process failed:', error);
    
    if (onProgress) {
      onProgress(0, 0, `Failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
      total_chunks: 0,
      processed: 0,
      failed: 0,
      errors: [error instanceof Error ? error.message : 'Unknown error']
    };
  }
}

/**
 * Browser console helper function
 */
export function runGMCTImport() {
  console.log('🚀 Starting GMCT data import from browser console...');
  
  importGMCTData('gmct', (processed, total, status) => {
    console.log(`📊 Progress: ${status}`);
  }).then(result => {
    console.log('🏁 Import finished:', result);
  }).catch(error => {
    console.error('💥 Import failed:', error);
  });
}

// Auto-run if in browser console context
if (typeof window !== 'undefined' && window.console) {
  console.log('📚 GMCT Data Import Script Loaded');
  console.log('💡 Run runGMCTImport() to start the import process');
  console.log('💡 Or call importGMCTData("gmct") directly');
}

// Default export for module usage
export default {
  importGMCTData,
  runGMCTImport,
  processDataIntoChunks,
  categorizeContent,
  extractMetadata,
  GMCT_SAMPLE_DATA,
  GMCT_FAQ_DATA
};